import { Container, Mesh, Program, IcoSphereGeometryBuffer, Texture } from 'beam';
import CubicBezier from './helpers/cubicBezier';
import BezierEasing from './helpers/BezierEasing';



import globeVertexShader from './shaders/globe-vs.glsl'
import globeFragmentShader from './shaders/globe-fs.glsl'
import getPosition from './helpers/getPosition'
import gridVertexShader from './shaders/grid-vs.glsl'
import gridFragmentShader from './shaders/grid-fs.glsl'

const easing = BezierEasing( 0.775, 0.065, 0.345, 0.880 );

export default class GLGlobe extends HTMLElement {

    constructor() {
        super();
        this.resize        = this.resize.bind(this);
        this.update        = this.update.bind(this);
        this._top = 0;

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp   = this.onPointerUp.bind(this);
        this._progress     = 0;
        this._progressDown = 0;
        this._pointerDown  = 0;
        this.userRotation = 0;

        this.autoRotateSpeed = 0;
    }

    connectedCallback() {
        this.__isConnected__ = true;

        let node = this;
        let i=0;
        while (node.parentNode && i<100) { 
            if (node.parentNode.tagName == 'WEBGL-SCENE') {
                this.$glScene = node.parentNode;
                break;
            }
            node = node.parentNode;
            i++;
        }

        if (!this.$glScene) {
            console.warn('missing <gl-scene> parent node');
            return
        }
     
        requestAnimationFrame(()=>{

            this.init();

            window.addEventListener('resize', this.resize);
            document.addEventListener('resize', this.resize);
            this.resizeObserver = new ResizeObserver(this.resize);
            this.resizeObserver.observe(this);
            this.resizeObserver.observe(document.body);
            

        this.addEventListener('mousedown', this.onPointerDown);
        this.addEventListener('touchstart', this.onPointerDown, {passive: false});

            this.update();
        })
    }


    onPointerDown(e) {
        if (!e.touches) {
            e.preventDefault();
            e.stopPropagation();
        }
        this._pointerDown = (e.touches ? e.touches[0].pageX : e.pageX)
        this._progressDown = this._progress; 
        document.addEventListener('mousemove', this.onPointerMove);
        document.addEventListener('touchmove', this.onPointerMove, {passive: false});
        document.addEventListener('mouseup',  this.onPointerUp);
        document.addEventListener('touchend', this.onPointerUp, {passive: false});
    }

    onPointerMove(e) {
        if (!e.touches) {
            e.preventDefault();
            e.stopPropagation();
        }
        this._progress = this._progressDown + ((e.touches ? e.touches[0].pageX : e.pageX) - this._pointerDown);
    }

    onPointerUp(e) {
        if (!e.touches) {
            e.preventDefault();
            e.stopPropagation();
        }
        document.removeEventListener('mousemove', this.onPointerMove);
        document.removeEventListener('touchmove', this.onPointerMove, {passive: false});
        document.removeEventListener('mouseup',   this.onPointerUp);
        document.removeEventListener('touchend',  this.onPointerUp, {passive: false});
    }

    init() {

        this.renderer = this.$glScene.renderer;
        this.container = new Container();
        this.$glScene.addChild(this.container);

        const options = {
            src: this.getAttribute("src") ? this.getAttribute('src') : '/wp-content/themes/wp-base-theme/dist/assets/textures/globe-map.jpg',
        }

        this.map = Texture.fromUrl(this.renderer.gl, options.src);

        const icoSphere = new Mesh();
        icoSphere.geometry = new IcoSphereGeometryBuffer(this.renderer.gl, {
            radius:         2000, 
            details:        64,
        });
        icoSphere.material = new Program(this.renderer.gl, {
            vertexShader:   globeVertexShader,
            fragmentShader: globeFragmentShader,
            type:           this.renderer.gl.POINTS,
            uniforms: {
                uPointScale:  window.innerWidth < 600 ? 0.05 : 0.4,
                tMap: this.map,
            }
        });
        this.icoSphere = icoSphere;
        this.container.add(icoSphere);

        this.bgSphereContainer = new Container();
        this.$glScene.addChild(this.bgSphereContainer);
        const bgSphere = new Mesh();
        bgSphere.geometry = new IcoSphereGeometryBuffer(this.renderer.gl, {
            radius:  2000, 
            details: 6,
        }); 
        bgSphere.material = new Program(this.renderer.gl, {
            vertexShader:   gridVertexShader,
            fragmentShader: gridFragmentShader,
            type:           this.renderer.gl.POINTS,
            uniforms: {
                uPointScale: 1,
                uSphere:     1,
            }
        });
        this.bgSphere = bgSphere;
        this.bgSphereContainer.add(this.bgSphere);

    }

    update() {
        if (!this.__isConnected__) {
            return;
        }
        requestAnimationFrame(()=>{
            this.update();
        });
        
        const enterProgress      = Math.max(0, Math.min(1, this.$glScene._scroll / (this._top - window.innerHeight*0.25)));
        const enterEasedProgress = easing(enterProgress);//CubicBezier( 0.775, 0.065, 0.345, 0.880, progress, 1 );


        const leaveprogress      = Math.max(0, Math.min(1, (this.$glScene._scroll - (this._top + window.innerHeight/2.)) / ( window.innerHeight )));
        const easedLeaveProgress = easing(leaveprogress);//CubicBezier( 0.775, 0.065, 0.345, 0.880, progress, 1 );

        const easedProgress = enterEasedProgress * (1. - easedLeaveProgress);


        const userRotation = this._progress / window.innerWidth * Math.PI * -2
        this.userRotation +=(userRotation - this.userRotation) * 0.1

        this.frameIndex = this.frameIndex || 0
        this.icoSphere.scale[0] +=(  ((window.innerWidth<600?200:300) / 2000) + (1.5 - 1.5*easedProgress)   - this.icoSphere.scale[0] ) * 0.1;
        this.icoSphere.scale[1] +=(  ((window.innerWidth<600?200:300) / 2000) + (1.5 - 1.5*easedProgress)   - this.icoSphere.scale[1] ) * 0.1;
        this.icoSphere.scale[2] +=(  ((window.innerWidth<600?200:300) / 2000) + (1.5 - 1.5*easedProgress)   - this.icoSphere.scale[2] ) * 0.1;
        this.icoSphere.rotation[1] = this.frameIndex * -0.0035 + this.userRotation * -1;

        const pX = window.innerWidth * -0.2;
        const pY = this._top * -1 + this.$glScene._scroll;

        this.container.position[0] = pX * easedProgress;
        this.container.position[1] = pY * easedProgress;

        this.container.rotation[2] = -0.3;

        this.container.updateMatrix();
        this.container.updateWorldMatrix();
        this.icoSphere.updateMatrix();
        this.icoSphere.updateWorldMatrix();

        this.frameIndex += easedProgress;
    
        this.icoSphere.material.setUniform('tMap', this.map);

        this.bgSphereContainer.position[0] = pX * easedProgress;
        this.bgSphereContainer.position[1] = pY * easedProgress;

        this.bgSphere.scale[0] +=( ((window.innerWidth<600?200:300) / 2000) + (1.5 - 1.5*easedProgress)  - this.bgSphere.scale[0] ) * 0.1;
        this.bgSphere.scale[1] +=( ((window.innerWidth<600?200:300) / 2000) + (1.5 - 1.5*easedProgress)  - this.bgSphere.scale[1] ) * 0.1;
        this.bgSphere.scale[2] +=( ((window.innerWidth<600?200:300) / 2000) + (1.5 - 1.5*easedProgress)  - this.bgSphere.scale[2] ) * 0.1;
        this.bgSphere.rotation[1] = this.frameIndex * -0.0035 + this.userRotation * -1;
        this.bgSphereContainer.rotation[2] = -0.3;
        this.icoSphere.updateMatrix();
        this.icoSphere.updateWorldMatrix();

        this.bgSphere.material.setUniform('uPointScale', 1 - Math.max(0., (easedProgress - 0.75) / 0.25));
        this.icoSphere.material.setUniform('uPointScale', (window.innerWidth < 600 ? 0.2 : 0.4) * Math.max(0., (easedProgress - 0.75) / 0.25));

    }

    disconnectedCallback() {
        this.__isConnected__ = false;
        document.removeEventListener('resize', this.resize);
        window.removeEventListener('resize', this.resize);
        this.$glScene.removeChild(this.container);
        this.$glScene.removeChild(this.bgSphereContainer);
        this.resizeObserver.unobserve(this);
        this.resizeObserver.unobserve(document.body);
    }

    resize() {
        this._top = getPosition(this).top;
    }

}

customElements.define('gl-globe', GLGlobe);